import React from 'react';
import BadgeFitment from 'src/components/BadgeFitment';
import { getCleanMarkdownString } from 'src/components/SproutsAgent/utils';
import AccountLogo from 'src/assets/icons/account-logo.svg';
import {
  capitalizeFirstLetterInAllWordsV2,
  contactLinkedInRegExV2,
  emailRegex,
  formatNumberToAbbreviations,
  isLongLink,
  isValidDomain,
  isValidLink,
} from 'src/utils/utilityMethods';
import 'github-markdown-css/github-markdown-dark.css';
import 'github-markdown-css/github-markdown-light.css';
import 'highlight.js/styles/github.css';
import Markdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';
import rehypeSlug from 'rehype-slug';
import remarkGfm from 'remark-gfm';
import remarkToc from 'remark-toc';
import { useSelector } from 'react-redux';
import { sproutsAgentSelector } from 'src/store/sprouts-agent/sprouts-agent.selector';
import Tooltip from 'src/components/Tooltip';
import { blackTooltipTheme } from 'src/model/componentTypes';

interface PropsType {
  message: any;
  idx: number;
}

const TableData: React.FC<PropsType> = React.memo(({ message, idx }) => {
  const [refinedMessage, setRefinedMessage] = React.useState('');
  const [keyValuePairs, setKeyValuePairs] = React.useState<{ key: string; value: string }[]>([]);
  const [plainText, setPlainText] = React.useState('');
  const [isTable, setIsTable] = React.useState(false);
  const { chatStreaming, messages } = useSelector(sproutsAgentSelector);
  const tableContainerRef = React.useRef<HTMLDivElement>(null);
  React.useLayoutEffect(() => {
    if (isTable && chatStreaming && tableContainerRef.current) {
      const container = tableContainerRef.current;
      container.scrollTop = container.scrollHeight;
    }
  }, [refinedMessage]);

  React.useEffect(() => {
    let cleanMessage: string = getCleanMarkdownString(message);
    const metaIndex = cleanMessage.indexOf('Metadata');
    if (metaIndex !== -1) {
      cleanMessage =
        cleanMessage.slice(0, metaIndex).replaceAll('*', '') +
        (cleanMessage.slice(metaIndex).split('Message:')[1] || '').replaceAll(`"`, '');
    }

    const isMarkdownTable = cleanMessage.trim().startsWith('|');
    const lines = cleanMessage.split(/\n+/).filter((line) => line.trim());
    const isKeyValue = lines.some((line) => line.includes(':'));

    if (isMarkdownTable) {
      setIsTable((prev) => (prev !== true ? true : prev));
      setRefinedMessage((prev) => (prev !== cleanMessage ? cleanMessage : prev));
      setKeyValuePairs((prev) => (prev.length !== 0 ? [] : prev));
      setPlainText((prev) => (prev !== '' ? '' : prev));
    } else if (isKeyValue) {
      setIsTable((prev) => (prev !== false ? false : prev));
      setRefinedMessage((prev) => (prev !== '' ? '' : prev));
      setPlainText((prev) => (prev !== '' ? '' : prev));

      let pairs: { key: string; value: string }[] = [];
      let currentKey = '';
      let currentValue = '';

      for (let i = 0; i < lines.length; i++) {
        const line = lines[i].trim();
        if (line.includes(':')) {
          if (currentKey) {
            pairs.push({ key: currentKey, value: currentValue.trim() });
          }
          const [key, ...valueParts] = line.split(':');
          currentKey = key.trim();
          currentValue = valueParts.join(':').trim();
        } else {
          if (currentKey) {
            pairs.push({ key: currentKey, value: currentValue.trim() });
            currentKey = '';
            currentValue = '';
          }
          pairs.push({ key: '-', value: line });
        }
      }
      if (currentKey) {
        pairs.push({ key: currentKey, value: currentValue.trim() });
      }
      setKeyValuePairs((prev) => (JSON.stringify(prev) !== JSON.stringify(pairs) ? pairs : prev));
    } else {
      setIsTable((prev) => (prev !== false ? false : prev));
      setRefinedMessage((prev) => (prev !== '' ? '' : prev));
      setKeyValuePairs((prev) => (prev.length !== 0 ? [] : prev));
      setPlainText((prev) => (prev !== cleanMessage ? cleanMessage : prev));
    }
  }, [message]);

  const renderNameCellData = (value: string) => {
    const match = value.match(/^(.*?)\s*\[(.*?)\]\s*\[(.*?)\]$/);
    if (match) {
      const label = match[1].trim();
      let rawUrl = match[2].trim();
      let summary = match[3].trim();
      if (!rawUrl.startsWith('http')) rawUrl = 'http://' + rawUrl;

      let domain;
      try {
        domain = new URL(rawUrl).hostname;
      } catch {
        return label;
      }

      return (
        <Tooltip
          placement="bottom"
          istheme={blackTooltipTheme}
          arrow
          title={summary}
          disableHoverListener={!(messages.length - 1 > idx || !chatStreaming)}
        >
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', cursor: 'pointer' }}>
            {(messages.length - 1 > idx || !chatStreaming) && (
              <img
                src={`https://logo.sprouts.ai/logo/${domain}`}
                alt={domain}
                onLoad={(e) => ((e.target as HTMLImageElement).style.opacity = '1')}
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.onerror = null;
                  target.src = AccountLogo;
                }}
                style={{
                  borderRadius: '4px',
                  border: '1px solid #e5e7eb',
                  objectFit: 'contain',
                  background: 'white',
                  verticalAlign: 'middle',
                  opacity: '0',
                }}
                height={23}
                width={23}
                onClick={() => window.open(rawUrl)}
              />
            )}
            <span>{label}</span>
          </div>
        </Tooltip>
      );
    }
    return value;
  };

  const getTableCellData = (children: any, cellIndex: number) => {
    let res: any = children;
    if (!children) return '';

    if (typeof children === 'string') {
      const text = children.toLowerCase();

      if (cellIndex === 1) return renderNameCellData(children);
      if (['high', 'medium', 'low'].includes(text)) return <BadgeFitment type={text as any} />;
      if (emailRegex.test(text)) {
        return (
          <span
            className="hyper-links"
            onClick={() => (window.location.href = `mailto:${text}`)}
            style={{ cursor: 'pointer', color: '#007bff' }}
          >
            {text}
          </span>
        );
      }
      if (isValidLink(text)) {
        return (
          <span
            className="hyper-links"
            onClick={() => window.open(text)}
            style={{ cursor: 'pointer', color: '#007bff' }}
          >
            {text}
          </span>
        );
      }
      if (isValidDomain(text)) {
        return (
          <span
            className="hyper-links"
            onClick={() => window.open(`https://www.${text}`)}
            style={{ cursor: 'pointer', color: '#007bff' }}
          >
            {text}
          </span>
        );
      }
      if (contactLinkedInRegExV2.test(text)) {
        const url = isLongLink(text) ? text : `https://www.${text}`;
        return (
          <span
            className="hyper-links"
            onClick={() => window.open(url)}
            style={{ cursor: 'pointer', color: '#007bff' }}
          >
            {text}
          </span>
        );
      }
      if (text.includes('$') && !/[a-z]/i.test(text)) {
        const range = text.split('-');
        return range
          .map(
            (item) =>
              '$' + formatNumberToAbbreviations(item.replaceAll(',', '').replaceAll('$', '')),
          )
          .join(' - ');
      }

      res = capitalizeFirstLetterInAllWordsV2(children);
    }

    return res;
  };

  const renderKeyValueValue = (key: string, value: string) => {
    if (/^\[.*\]$/.test(value)) {
      return null;
    }
    return getTableCellData(value, key === 'Name' ? 1 : 0);
  };

  const rehypePlugins = [rehypeSlug, rehypeRaw, rehypeSanitize];
  const remarkPlugins = [remarkToc, remarkGfm];

  const memoizedMarkdown = React.useMemo(() => {
    return (
      <Markdown
        rehypePlugins={rehypePlugins}
        remarkPlugins={remarkPlugins}
        components={{
          table: ({ children }) => (
            <div
              ref={tableContainerRef}
              style={{ width: '100%', overflow: 'auto', maxHeight: '400px', position: 'relative' }}
            >
              <table style={{ borderCollapse: 'collapse', width: '100%' }}>{children}</table>
            </div>
          ),
          th: ({ children, ...props }) => {
            const isFirstColumn = props.node?.position?.start?.column === 1;
            let cleanedChildren = children;
            if (isFirstColumn && typeof children === 'string') {
              cleanedChildren = children.replace(/\[.*?\]/g, '').trim();
            }
            return (
              <th
                style={{
                  borderBottom: '0.73px solid #0000000F',
                  padding: '8px 16px 8px 8px',
                  textAlign: 'left',
                  fontSize: '14.62px',
                  fontWeight: '700',
                  color: '#000000',
                  whiteSpace: 'normal',
                  minWidth: isFirstColumn ? '150px' : '120px',
                  maxWidth: isFirstColumn ? '200px' : '180px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  position: 'sticky',
                  top: 0,
                  background: 'white',
                  zIndex: isFirstColumn ? 10 : 5,
                  ...(isFirstColumn ? { left: 0, boxShadow: '2px 0 4px rgba(0, 0, 0, 0.1)' } : {}),
                }}
              >
                {cleanedChildren}
              </th>
            );
          },
          td: ({ children, ...props }) => {
            const isFirstColumn = props.node?.position?.start?.column === 1;
            return (
              <td
                style={{
                  borderBottom: '0.73px solid #0000000F',
                  padding: '8px 16px 8px 8px',
                  textAlign: 'left',
                  fontSize: '12px',
                  color: 'black',
                  minWidth: isFirstColumn ? '150px' : '120px',
                  maxWidth: isFirstColumn ? '200px' : '300px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  position: isFirstColumn ? 'sticky' : undefined,
                  left: isFirstColumn ? 0 : undefined,
                  zIndex: isFirstColumn ? 4 : undefined,
                  boxShadow: isFirstColumn ? '2px 0 4px rgba(0, 0, 0, 0.1)' : undefined,
                  fontWeight: isFirstColumn ? '700' : '500',
                  background: isFirstColumn ? 'white' : 'transparent',
                }}
              >
                {getTableCellData(children, props.node?.position?.start?.column || 0)}
              </td>
            );
          },
          code: ({ children }) => (
            <div
              style={{
                overflowX: 'auto',
                whiteSpace: 'pre',
                fontSize: '14px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                padding: '8px',
                margin: '8px 0',
              }}
            >
              {children}
            </div>
          ),
        }}
      >
        {refinedMessage}
      </Markdown>
    );
  }, [refinedMessage, messages.length, chatStreaming]);

  return (
    <div>
      {isTable ? (
        memoizedMarkdown
      ) : keyValuePairs.length > 0 ? (
        <div style={{ fontSize: '14px', lineHeight: '1.5' }}>
          {keyValuePairs.map(({ key, value }, index) => (
            <div key={index} style={{ marginBottom: '8px' }}>
              {key !== '-' && <strong>{key + ' '}</strong>}
              {renderKeyValueValue(key, value)}
              {key !== '-' && <br />}
            </div>
          ))}
        </div>
      ) : (
        <div style={{ fontSize: '14px', lineHeight: '1.5', whiteSpace: 'pre-wrap' }}>
          {plainText}
        </div>
      )}
    </div>
  );
});
TableData.displayName = 'TableData';
export default TableData;
